import Popper, { type PopperProps } from "@mui/material/Popper";
import { alpha, useTheme } from "@mui/material";
import {
    adTokenLabel,
    isCopywriterInstruction,
    isMidWordBracket,
    type AdTokenContext,
    type AdTokenMatch,
} from "./adTokens";

/**
 * The menu looks like the app's other option menus -- the MUI Autocomplete behind
 * ApprovedUrlPicker, in the same panel -- so it is built from the same theme values: the
 * Paper's elevation-1 shadow and 4px radius, body1 type, a listbox padded 8px top and
 * bottom, 6px 16px rows, and the selected row tinted with the primary color at the theme's
 * selected opacity (0.08). Primary is the brand purple, #845adf.
 *
 * The token itself is the purple's dark shade, which stays AA-readable on the tinted row
 * (about 7:1). The amber "you fill this in" pill and the mid-word warning are not chrome --
 * amber means "needs a person" in the field notes too (MPN-1930) -- so they stay amber.
 */
function usePickerStyle() {
    const theme = useTheme();
    return {
        paper: {
            ...theme.typography.body1,
            background: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[1],
        },
        selected: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
        token: theme.palette.primary.dark,
        text: theme.palette.text.primary,
        secondary: theme.palette.text.secondary,
        divider: theme.palette.divider,
    };
}

/** The ad token picker's listbox (MPN-1932), hung off whatever the field anchors it to. */
export default function AdTokenMenu({ anchorEl, value, context, matches, active, onActive, onInsert }: {
    anchorEl: PopperProps["anchorEl"];
    /** The field's text, for the mid-word warning. */
    value: string;
    context: AdTokenContext | null;
    matches: AdTokenMatch[];
    active: number;
    onActive: (index: number) => void;
    onInsert: (index: number) => void;
}) {
    const picker = usePickerStyle();
    return (
        <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400 }}
            modifiers={[{ name: "offset", options: { offset: [0, 6] } }]}
        >
            <div
                role="listbox"
                aria-label="Ad tokens"
                style={{
                    ...picker.paper,
                    width: 340, maxWidth: "90vw", overflow: "hidden",
                }}
            >
                <div style={{ maxHeight: "40vh", overflowY: "auto", padding: "8px 0" }}>
                    {matches.length === 0 ? (
                        <div style={{ padding: "14px 16px", fontSize: "0.875rem", color: picker.secondary }}>
                            No ad token matches "{context?.query}". Keep typing -- it stays
                            exactly as you wrote it.
                        </div>
                    ) : (
                        matches.map((match, i) => (
                            <button
                                key={match.token.id}
                                type="button"
                                role="option"
                                aria-selected={i === active}
                                // mousedown, not click: a click would blur the field first,
                                // which closes the menu and fires an autosave.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    onInsert(i);
                                }}
                                onMouseMove={() => onActive(i)}
                                style={{
                                    display: "block", width: "100%", textAlign: "left",
                                    border: 0, padding: "6px 16px",
                                    cursor: "pointer", font: "inherit", color: picker.text,
                                    background: i === active ? picker.selected : "transparent",
                                }}
                            >
                                <span style={{ fontSize: "0.875rem", color: picker.token }}>
                                    {match.token.token}
                                </span>
                                {/*
                                    A copywriter instruction is offered like any other token
                                    and inserts the same way, but nothing will ever resolve it
                                    (MPN-1952). Say so on the row: the help text below reads as
                                    a hint either way, and without this the menu quietly
                                    promises a bracket that will still be sitting there at
                                    publish.
                                */}
                                {isCopywriterInstruction(match.token) && (
                                    <span
                                        style={{
                                            marginLeft: 6, padding: "0 5px", borderRadius: 4,
                                            background: "#fdf3e3", color: "#8f5405",
                                            fontSize: 10, fontWeight: 600,
                                            verticalAlign: "middle", whiteSpace: "nowrap",
                                        }}
                                    >
                                        you fill this in
                                    </span>
                                )}
                                {match.token.help_text && (
                                    <span style={{ display: "block", fontSize: "0.75rem", color: picker.secondary }}>
                                        {match.token.help_text}
                                    </span>
                                )}
                                {/* Say which spelling matched and which one lands, or the
                                    writer sees a different token from the one they typed. */}
                                {match.alias && (
                                    <span style={{ display: "block", fontSize: "0.75rem", color: picker.secondary }}>
                                        matches alias "{adTokenLabel(match.alias)}" -- inserts{" "}
                                        {match.token.token}
                                    </span>
                                )}
                            </button>
                        ))
                    )}
                </div>
                <div style={{
                    borderTop: `1px solid ${picker.divider}`,
                    padding: "6px 16px", fontSize: "0.75rem", color: picker.secondary,
                    display: "flex", flexWrap: "wrap", gap: "2px 10px",
                }}>
                    <span>Up/Down move</span>
                    <span>Enter inserts</span>
                    <span>Esc keeps [ literal</span>
                    {context && isMidWordBracket(value, context.start) && (
                        <span style={{ color: "#8f5405" }}>
                            typed mid-word -- the token will land inside that word
                        </span>
                    )}
                </div>
            </div>
        </Popper>
    );
}
